import { StyleSheet } from "react-native";

import { useTranslation } from "@luna-restaurant/i18n";
import type {
  ThemedTableColumn,
  ThemedTableHeaderColumn,
} from "@luna-restaurant/ui";

import { CancelConfirmationModal } from "@/components/modals/CancelConfirmationModal";

export type CancelLunchBoxModalOrder = {
  id: string;
  deliveryDateRaw: string;
  deliveryDate: string;
  deliveryTime: string;
  quantity: string;
  lunchPaketName: string;
  classLabel: string;
  address: string;
};

type CancelLunchBoxModalProps = {
  visible: boolean;
  order: CancelLunchBoxModalOrder | null;
  isSubmitting?: boolean;
  validationError?: string | null;
  onDismiss: () => void;
  onConfirm: (order: CancelLunchBoxModalOrder) => void;
};

export function CancelLunchBoxModal({
  visible,
  order,
  isSubmitting = false,
  validationError = null,
  onDismiss,
  onConfirm,
}: CancelLunchBoxModalProps) {
  const { t } = useTranslation();

  if (!order) {
    return null;
  }

  const headerColumns: ThemedTableHeaderColumn[] = [
    {
      width: 21,
      parts: [
        {
          label: t("lunchBoxes.table.header.deliveryDate"),
          style: styles.tableHeaderBold,
        },
        {
          label: t("lunchBoxes.table.header.deliveryTime"),
          style: styles.tableHeaderMedium,
        },
      ],
    },
    {
      width: 21,
      parts: [
        {
          label: t("lunchBoxes.table.header.quantity"),
          style: styles.tableHeaderBold,
        },
        {
          label: t("lunchBoxes.table.header.lunchPaketName"),
          style: styles.tableHeaderMedium,
        },
      ],
    },
    {
      width: 37,
      parts: [
        {
          label: t("lunchBoxes.table.header.schoolClass"),
          style: styles.tableHeaderBold,
        },
        {
          label: t("lunchBoxes.table.header.address"),
          style: styles.tableHeaderMedium,
        },
      ],
    },
  ];

  const rowColumns: ThemedTableColumn[] = [
    {
      type: "text",
      parts: [
        { label: order.deliveryDate, style: styles.tableCellBold },
        { label: order.deliveryTime },
      ],
    },
    {
      type: "text",
      parts: [
        { label: order.quantity, style: styles.tableCellBold },
        { label: order.lunchPaketName },
      ],
    },
    {
      type: "text",
      parts: [
        { label: order.classLabel, style: styles.tableCellBold },
        { label: order.address },
      ],
    },
  ];

  return (
    <CancelConfirmationModal
      visible={visible}
      translationPrefix="lunchBoxes.cancelModal"
      headerColumns={headerColumns}
      rowColumns={rowColumns}
      isSubmitting={isSubmitting}
      validationError={validationError}
      onDismiss={onDismiss}
      onConfirm={() => onConfirm(order)}
    />
  );
}

const styles = StyleSheet.create({
  tableHeaderBold: {
    fontWeight: "bold",
    fontFamily: "PoppinsBold",
  },
  tableHeaderMedium: {
    fontFamily: "PoppinsMedium",
    fontWeight: "500",
  },
  tableCellBold: {
    fontWeight: "bold",
    fontFamily: "PoppinsBold",
  },
});
